import { useFindAllSupportPurchasesQuery } from '../../api/api-hooks';
import { DataGrid, GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import { CircularProgress } from '@mui/material';
import React from 'react';
import { Item } from '../../utils/sharedTypes';
import { SupportPurchase, Order, License } from '../../api/api-generated';
import {
  differenceBetweenDateStringsInDays,
  truncateISOString,
} from '../../utils/dateTime';
import { SupportPurchaseFilter } from './supportPurchasesTypes';
import { filterSupportPurchase } from './supportPurchasesUtils';

interface Props {
  setSelectedSupportPurchases: React.Dispatch<React.SetStateAction<Item[]>>;
  extraFilters: {
    license?: License;
    excludeLicense?: boolean;
    order?: Order;
    excludeOrder?: boolean;
  };
  filters?: SupportPurchaseFilter;
}

const SupportPurchasesTable = ({
  setSelectedSupportPurchases,
  extraFilters,
  filters,
}: Props) => {
  const { data: allSupportPurchases } = useFindAllSupportPurchasesQuery({});
  if (allSupportPurchases === undefined)
    return <CircularProgress></CircularProgress>;
  const filteredSupportPurchases = allSupportPurchases.filter(
    (supportPurchase) => {
      if (!filterSupportPurchase(supportPurchase, filters)) return false;
      if (extraFilters.license) {
        if (
          supportPurchase.softwareId === extraFilters.license.softwareId &&
          supportPurchase.companyId === extraFilters.license.companyId
        ) {
          if (extraFilters.excludeLicense) return false;
          else return true;
        } else {
          if (extraFilters.excludeLicense) return true;
          else return false;
        }
      }
      if (extraFilters.order) {
        if (supportPurchase.orderId === extraFilters.order.id) {
          if (extraFilters.excludeOrder) return false;
          else return true;
        } else {
          if (extraFilters.excludeOrder) return true;
          else return false;
        }
      }
      return true;
    }
  );
  // Define data for display in DataGrid
  const columns: GridColDef[] = extraFilters.order
    ? [
        { field: 'company', headerName: 'Company', width: 150 },
        { field: 'software', headerName: 'Software', width: 180 },
        { field: 'supportStartDate', headerName: 'Start Date', width: 180 },
        { field: 'supportDuration', headerName: 'Duration', width: 180 },
        { field: 'hardware', headerName: 'Hardware', flex: 1 },
      ]
    : [
        { field: 'order', headerName: 'Order', width: 150 },
        { field: 'supportStartDate', headerName: 'Start Date', width: 180 },
        { field: 'supportDuration', headerName: 'Duration', width: 180 },
        { field: 'hardware', headerName: 'Hardware', flex: 1 },
      ];
  const rows = filteredSupportPurchases.map((item: SupportPurchase) => ({
    ...item,
    company: item.company ? item.company.companyName : 'None',
    software: item.software.type,
    order: item.order ? item.order.purchaseOrderName : 'None',
    supportStartDate:
      item.supportStartDate === null
        ? 'None'
        : truncateISOString(item.supportStartDate),
    supportDuration:
      item.supportEndDate === null || item.supportStartDate === null
        ? 'None'
        : differenceBetweenDateStringsInDays(
            item.supportEndDate,
            item.supportStartDate
          ) + ' Days',
    hardware: item.hardware
      .reduce((acc, cur) => acc + cur.serial + ', ', '')
      .slice(0, -2),
  }));
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedSupportPurchases(
      filteredSupportPurchases.filter((supportPurchase) =>
        model.includes(supportPurchase.id)
      )
    );
  };
  return (
    <div style={{ paddingBottom: '1rem', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        disableColumnMenu={true}
        onSelectionModelChange={handleTableSelection}
      />
    </div>
  );
};

export default SupportPurchasesTable;
